import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiPlus, FiTrash2, FiCalendar, FiMapPin, FiUsers, FiDollarSign } from 'react-icons/fi';

// API configuration
const API_URL = process.env.REACT_APP_API_URL || '/api';

const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['show'].includes(prop)
})`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['show'].includes(prop)
})`
  background: white;
  border-radius: ${props => props.theme.borders.radius.lg};
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  transform: ${props => props.show ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)'};
  transition: transform 0.3s ease;
`;

const ModalHeader = styled.div`
  padding: ${props => props.theme.spacing.xl};
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: 700;
  color: ${props => props.theme.colors.gray[800]};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.gray[600]};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borders.radius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.gray[800]};
  }
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const Form = styled.form`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
`;

const FormRow = styled.div.withConfig({
  shouldForwardProp: (prop) => !['columns'].includes(prop)
})`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr'};
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 600;
  color: ${props => props.theme.colors.gray[700]};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }

  &:invalid {
    border-color: ${props => props.theme.colors.danger};
  }
`;

const TextArea = styled.textarea`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 100;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
    z-index: 200;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[700]};
  cursor: pointer;
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: 600;
  color: ${props => props.theme.colors.gray[800]};
  margin: ${props => props.theme.spacing.xl} 0 ${props => props.theme.spacing.lg} 0;
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
`;

const TicketTypeCard = styled.div`
  background: ${props => props.theme.colors.gray[50]};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing.lg};
  position: relative;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.md};
  
  h4 {
    margin: 0;
    font-size: ${props => props.theme.fontSizes.md};
    font-weight: 600;
    color: ${props => props.theme.colors.gray[800]};
  }
`;

const RemoveTicketButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing.sm};
  right: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.danger};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borders.radius.sm};
  padding: ${props => props.theme.spacing.xs};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.danger};
    opacity: 0.8;
  }
`;

const AddTicketButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md};
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: 1px dashed ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: ${props => props.theme.colors.gray[50]};
  }
`;

const ModalFooter = styled.div`
  padding: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !['variant'].includes(prop),
})`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borders.radius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: ${props.theme.colors.primary};
    color: white;
    
    &:hover:not(:disabled) {
      background: ${props.theme.colors.gray[800]};
    }
  ` : `
    background: transparent;
    color: ${props.theme.colors.gray[600]};
    border: 1px solid ${props.theme.colors.gray[300]};
    
    &:hover {
      background: ${props.theme.colors.gray[50]};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.danger};
  font-size: ${props => props.theme.fontSizes.sm};
  margin-top: ${props => props.theme.spacing.xs};
`;

// Security Modal Styles
const SecurityModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['show'].includes(prop)
})`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const SecurityModalContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['show'].includes(prop)
})`
  background: white;
  border-radius: ${props => props.theme.borders.radius.lg};
  max-width: 500px;
  width: 90%;
  transform: ${props => props.show ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)'};
  transition: transform 0.3s ease;
`;

const SecurityInfoBox = styled.div`
  background: ${props => props.theme.colors.gray[50]};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[600]};
`;

const SetupSecurityButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 500;
  transition: all 0.2s ease;
  margin-top: ${props => props.theme.spacing.sm};

  &:hover {
    background: ${props => props.theme.colors.gray[800]};
  }
`;

const CreateEventModal = ({ isOpen, onClose, onSuccess, eventToEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    date: '',
    time: '',
    venue: '',
    location: '',
    category: 'general',
    maxCapacity: '',
    image: null,
    taxEnabled: false,
    taxRate: 5,
    referenceEnabled: false,
    transportationEnabled: false,
    inviteOnly: false,
    securityInfo: '',
    pinGenerationFrequency: 'daily',
    pinNotificationEmail: ''
  });

  // Track original data and changes
  const [originalTicketTypes, setOriginalTicketTypes] = useState([]);
  const [ticketTypeChanges, setTicketTypeChanges] = useState(new Set());
  const [removedTicketTypes, setRemovedTicketTypes] = useState(new Set());
  
  // Track add-ons changes
  const [originalAddOns, setOriginalAddOns] = useState([]);
  const [addOnChanges, setAddOnChanges] = useState(new Set());
  const [removedAddOns, setRemovedAddOns] = useState(new Set());

  const [ticketTypes, setTicketTypes] = useState([
    {
      typeName: 'General Admission',
      description: '',
      price: '',
      totalQuantity: '',
      soldQuantity: 0,
      features: [],
      expiryDate: '',
      sortOrder: 0,
      allocationField: '',
      minPersons: 1,
      minAge: 0,
      maxAge: 120
    }
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSecurityModal, setShowSecurityModal] = useState(false);

  const [addOns, setAddOns] = useState([]);

  // Populate form when editing
  useEffect(() => {
    console.log('🔄 useEffect triggered - eventToEdit:', eventToEdit);
    if (eventToEdit) {
      console.log('📝 Populating form with event data:', {
        name: eventToEdit.name,
        taxEnabled: eventToEdit.taxEnabled,
        taxRate: eventToEdit.taxRate
      });
      
      setFormData({
        name: eventToEdit.name || '',
        description: eventToEdit.description || '',
        date: eventToEdit.date ? eventToEdit.date.split('T')[0] : '',
        time: eventToEdit.time || '',
        venue: eventToEdit.venue || '',
        location: eventToEdit.location || '',
        category: eventToEdit.category || 'general',
        maxCapacity: eventToEdit.maxCapacity || '',
        image: null, // Don't pre-populate file input
        taxEnabled: Boolean(eventToEdit.taxEnabled), // Ensure boolean value
        taxRate: eventToEdit.taxRate || 5,
        referenceEnabled: Boolean(eventToEdit.referenceEnabled),
        transportationEnabled: Boolean(eventToEdit.transportationEnabled),
        inviteOnly: Boolean(eventToEdit.inviteOnly),
        securityInfo: eventToEdit.securityInfo || '',
        pinGenerationFrequency: eventToEdit.pinGenerationFrequency || 'daily',
        pinNotificationEmail: eventToEdit.pinNotificationEmail || ''
      });
      
      console.log('✅ Form data set, taxEnabled:', eventToEdit.taxEnabled, 'taxRate:', eventToEdit.taxRate);
      
      if (eventToEdit.ticketTypes && eventToEdit.ticketTypes.length > 0) {
        const processedTicketTypes = eventToEdit.ticketTypes.map(ticket => {
          const soldQuantity = (ticket.totalQuantity || 0) - (ticket.availableQuantity || 0);
          return {
            _id: ticket._id, // Keep the ID for tracking
            typeName: ticket.typeName || '',
            description: ticket.description || '',
            price: ticket.price || '',
            totalQuantity: ticket.totalQuantity || '',
            soldQuantity: soldQuantity, // Calculate sold tickets
            features: ticket.features || [],
            expiryDate: ticket.expiryDate ? ticket.expiryDate.split('T')[0] : '',
            sortOrder: ticket.sortOrder || 0,
            allocationField: ticket.allocationField || '',
            minPersons: ticket.minPersons || 1,
            minAge: ticket.minAge || 0,
            maxAge: ticket.maxAge || 120
          };
        });
        
        setTicketTypes(processedTicketTypes);
        setOriginalTicketTypes(JSON.parse(JSON.stringify(processedTicketTypes))); // Deep copy
        setTicketTypeChanges(new Set()); // Clear changes
        setRemovedTicketTypes(new Set()); // Clear removals
      }

      // Load existing add-ons if editing
      if (eventToEdit._id) {
        fetchEventAddOns(eventToEdit._id);
      }
    } else {
      // Reset form for creating new event
      setFormData({
        name: '',
        description: '',
        date: '',
        time: '',
        venue: '',
        location: '',
        category: 'general',
        maxCapacity: '',
        image: null,
        taxEnabled: false,
        taxRate: 5,
        referenceEnabled: false,
        transportationEnabled: false,
        inviteOnly: false,
        securityInfo: '',
        pinGenerationFrequency: 'daily',
        pinNotificationEmail: ''
      });
      
      setTicketTypes([
        {
          typeName: 'General Admission',
          description: '',
          price: '',
          totalQuantity: '',
          features: [],
          expiryDate: '',
          sortOrder: 0,
          allocationField: '',
          minPersons: 1,
          minAge: 0,
          maxAge: 120
        }
      ]);

      setAddOns([]);
    }
    setError('');
  }, [eventToEdit]);

  const handleInputChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    console.log('📝 handleInputChange called:', { name, value, type, checked });
    
    let fieldValue;
    if (type === 'file') {
      fieldValue = files[0];
    } else if (type === 'checkbox') {
      fieldValue = checked;
    } else {
      fieldValue = value;
    }
    
    console.log('🔄 Setting field:', name, 'to value:', fieldValue);
    
    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));
  };

  const handleTicketTypeChange = (index, field, value) => {
    // Special validation for totalQuantity field
    if (field === 'totalQuantity') {
      const currentTicket = ticketTypes[index];
      const soldQuantity = currentTicket.soldQuantity || 0;
      const newQuantity = parseInt(value) || 0;
      
      if (soldQuantity > 0 && newQuantity < soldQuantity) {
        alert(`Cannot reduce total quantity below ${soldQuantity}. ${soldQuantity} tickets have already been sold.`);
        return;
      }
    }
    
    // Track which ticket types have been modified
    const currentTicket = ticketTypes[index];
    if (currentTicket._id) {
      setTicketTypeChanges(prev => new Set(prev).add(currentTicket._id));
    }
    
    setTicketTypes(prev => prev.map((ticket, i) => 
      i === index ? { ...ticket, [field]: value } : ticket
    ));
  };

  const addTicketType = () => {
    const newTicketId = `new_${Date.now()}`; // Temporary ID for new tickets
    setTicketTypes(prev => [...prev, {
      _id: newTicketId,
      typeName: '',
      description: '',
      price: '',
      totalQuantity: '',
      soldQuantity: 0, // New tickets have 0 sold
      features: [],
      expiryDate: '',
      sortOrder: prev.length,
      allocationField: '',
      minPersons: 1,
      minAge: 0,
      maxAge: 120,
      isNew: true // Mark as new ticket
    }]);
    
    // Mark this new ticket as changed
    setTicketTypeChanges(prev => new Set(prev).add(newTicketId));
  };

  const removeTicketType = (index) => {
    const ticketToRemove = ticketTypes[index];
    
    // Check if any tickets have been sold
    if (ticketToRemove.soldQuantity > 0) {
      alert(`Cannot delete "${ticketToRemove.typeName}" ticket type. ${ticketToRemove.soldQuantity} tickets have already been sold.`);
      return;
    }
    
    if (ticketTypes.length > 1) {
      // Track removal if it's an existing ticket type (has real ID, not temporary)
      if (ticketToRemove._id && !ticketToRemove.isNew) {
        setRemovedTicketTypes(prev => new Set(prev).add(ticketToRemove._id));
      }
      
      // Remove from changes tracking
      setTicketTypeChanges(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketToRemove._id);
        return newSet;
      });
      
      setTicketTypes(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Add-ons functions
  const fetchEventAddOns = async (eventId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/events/${eventId}/addons`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const processedAddOns = (data.addOns || []).map(addOn => ({
          ...addOn,
          _id: addOn._id, // Keep the ID for tracking
          bookedQuantity: addOn.currentlyBooked || 0 // Track booked quantity
        }));
        
        setAddOns(processedAddOns);
        setOriginalAddOns(JSON.parse(JSON.stringify(processedAddOns))); // Deep copy
        setAddOnChanges(new Set()); // Clear changes
        setRemovedAddOns(new Set()); // Clear removals
      }
    } catch (error) {
      console.error('Error fetching add-ons:', error);
    }
  };

  const handleAddOnChange = (index, field, value) => {
    // Special validation for totalAvailable field
    if (field === 'totalAvailable') {
      const currentAddOn = addOns[index];
      const bookedQuantity = currentAddOn.bookedQuantity || 0;
      const newQuantity = value ? parseInt(value) : null;
      
      if (bookedQuantity > 0 && newQuantity !== null && newQuantity < bookedQuantity) {
        alert(`Cannot reduce total available below ${bookedQuantity}. ${bookedQuantity} add-ons have already been booked.`);
        return;
      }
    }
    
    // Track which add-ons have been modified
    const currentAddOn = addOns[index];
    if (currentAddOn._id) {
      setAddOnChanges(prev => new Set(prev).add(currentAddOn._id));
    }
    
    setAddOns(prev => prev.map((addOn, i) => {
      if (i === index) {
        const updatedAddOn = { ...addOn, [field]: value };
        
        // Auto-manage requiresAttendeeAssociation based on associationType
        if (field === 'associationType') {
          if (value === 'per-attendee') {
            updatedAddOn.requiresAttendeeAssociation = true;
          } else {
            updatedAddOn.requiresAttendeeAssociation = false;
          }
        }
        
        return updatedAddOn;
      }
      return addOn;
    }));
  };

  const addAddOn = () => {
    const newAddOnId = `new_addon_${Date.now()}`; // Temporary ID for new add-ons
    setAddOns(prev => [...prev, {
      _id: newAddOnId,
      name: '',
      description: '',
      price: '',
      currency: 'DHS',
      category: 'other',
      maxQuantityPerTicket: 10,
      totalAvailable: null,
      bookedQuantity: 0, // New add-ons have 0 booked
      sortOrder: prev.length,
      associationType: 'global',
      requiresAttendeeAssociation: false,
      isNew: true // Mark as new add-on
    }]);
    
    // Mark this new add-on as changed
    setAddOnChanges(prev => new Set(prev).add(newAddOnId));
  };

  const removeAddOn = (index) => {
    const addOnToRemove = addOns[index];
    
    // Check if any add-ons have been booked
    if (addOnToRemove.bookedQuantity > 0) {
      alert(`Cannot delete "${addOnToRemove.name}" add-on. ${addOnToRemove.bookedQuantity} have already been booked.`);
      return;
    }
    
    // Track removal if it's an existing add-on (has real ID, not temporary)
    if (addOnToRemove._id && !addOnToRemove.isNew) {
      setRemovedAddOns(prev => new Set(prev).add(addOnToRemove._id));
    }
    
    // Remove from changes tracking
    setAddOnChanges(prev => {
      const newSet = new Set(prev);
      newSet.delete(addOnToRemove._id);
      return newSet;
    });
    
    setAddOns(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get auth token
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Create FormData for file upload
      const eventFormData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          eventFormData.append(key, formData[key]);
        } else if (key === 'taxEnabled' || key === 'taxRate' || key === 'referenceEnabled' || key === 'transportationEnabled' || key === 'inviteOnly') {
          // Always include these fields, even if false or 0
          eventFormData.append(key, formData[key]);
        }
      });

      let eventResult;
      
      if (eventToEdit) {
        // Update existing event
        const eventResponse = await fetch(`${API_URL}/events/${eventToEdit._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: eventFormData
        });

        if (!eventResponse.ok) {
          const eventError = await eventResponse.json();
          throw new Error(eventError.message || 'Failed to update event');
        }

        eventResult = await eventResponse.json();
      } else {
        // Create new event
        const eventResponse = await fetch(`${API_URL}/events`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: eventFormData
        });

        if (!eventResponse.ok) {
          const eventError = await eventResponse.json();
          throw new Error(eventError.message || 'Failed to create event');
        }

        eventResult = await eventResponse.json();
      }

      const eventId = eventResult.event._id;

      // Handle ticket types - only update changed ones
      if (eventToEdit && (ticketTypeChanges.size > 0 || removedTicketTypes.size > 0)) {
        // First, handle deletions
        for (const removedId of removedTicketTypes) {
          const response = await fetch(`${API_URL}/events/${eventId}/ticket-types/${removedId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            console.warn('Failed to delete ticket type:', removedId);
          }
        }

        // Then handle updates and new creations
        for (const changedId of ticketTypeChanges) {
          const changedTicket = ticketTypes.find(t => t._id === changedId);
          if (!changedTicket) continue;

          if (changedTicket.isNew) {
            // Create new ticket type
            const newTicketData = {
              typeName: changedTicket.typeName,
              description: changedTicket.description,
              price: parseFloat(changedTicket.price),
              totalQuantity: parseInt(changedTicket.totalQuantity),
              expiryDate: changedTicket.expiryDate || null,
              features: changedTicket.features,
              sortOrder: changedTicket.sortOrder,
              allocationField: changedTicket.allocationField,
              minPersons: parseInt(changedTicket.minPersons) || 1,
              minAge: parseInt(changedTicket.minAge) || 0,
              maxAge: parseInt(changedTicket.maxAge) || 120
            };

            console.log('📤 Creating new ticket type:', newTicketData);
            const response = await fetch(`${API_URL}/events/${eventId}/ticket-types`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ ticketTypes: [newTicketData] })
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
              console.error('❌ Failed to create new ticket type:', changedTicket.typeName, errorData);
              throw new Error(`Failed to create ticket type "${changedTicket.typeName}": ${errorData.message || 'Unknown error'}`);
            } else {
              const result = await response.json();
              console.log('✅ Ticket type created:', result);
            }
          } else {
            // Update existing ticket type
            const originalTicket = originalTicketTypes.find(t => t._id === changedId);
            const updateData = {};

            // Only include fields that actually changed
            if (changedTicket.typeName !== originalTicket.typeName) updateData.typeName = changedTicket.typeName;
            if (changedTicket.description !== originalTicket.description) updateData.description = changedTicket.description;
            if (parseFloat(changedTicket.price) !== parseFloat(originalTicket.price)) updateData.price = parseFloat(changedTicket.price);
            if (parseInt(changedTicket.totalQuantity) !== parseInt(originalTicket.totalQuantity)) updateData.totalQuantity = parseInt(changedTicket.totalQuantity);
            if (changedTicket.expiryDate !== originalTicket.expiryDate) updateData.expiryDate = changedTicket.expiryDate || null;
            if (JSON.stringify(changedTicket.features) !== JSON.stringify(originalTicket.features)) updateData.features = changedTicket.features;
            if (changedTicket.allocationField !== originalTicket.allocationField) updateData.allocationField = changedTicket.allocationField;
            if (parseInt(changedTicket.minPersons) !== parseInt(originalTicket.minPersons)) updateData.minPersons = parseInt(changedTicket.minPersons) || 1;
            if (parseInt(changedTicket.minAge) !== parseInt(originalTicket.minAge)) updateData.minAge = parseInt(changedTicket.minAge) || 0;
            if (parseInt(changedTicket.maxAge) !== parseInt(originalTicket.maxAge)) updateData.maxAge = parseInt(changedTicket.maxAge) || 120;
            if (changedTicket.sortOrder !== originalTicket.sortOrder) updateData.sortOrder = changedTicket.sortOrder;

            // Only send update if there are actual changes
            if (Object.keys(updateData).length > 0) {
              console.log('📤 Updating ticket type:', changedTicket.typeName, updateData);
              const response = await fetch(`${API_URL}/events/${eventId}/ticket-types/${changedId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updateData)
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
                console.error('❌ Failed to update ticket type:', changedTicket.typeName, errorData);
                throw new Error(`Failed to update ticket type "${changedTicket.typeName}": ${errorData.message || 'Unknown error'}`);
              } else {
                const result = await response.json();
                console.log('✅ Ticket type updated:', result);
              }
            }
          }
        }
      } else if (!eventToEdit) {
        // For new events, create all valid ticket types
        const validTicketTypes = ticketTypes.filter(ticket => 
          ticket.typeName && ticket.price && ticket.totalQuantity
        ).map(ticket => ({
          typeName: ticket.typeName,
          description: ticket.description,
          price: parseFloat(ticket.price),
          totalQuantity: parseInt(ticket.totalQuantity),
          expiryDate: ticket.expiryDate || null,
          features: ticket.features,
          sortOrder: ticket.sortOrder,
          allocationField: ticket.allocationField,
          minPersons: parseInt(ticket.minPersons) || 1,
          minAge: parseInt(ticket.minAge) || 0,
          maxAge: parseInt(ticket.maxAge) || 120
        }));

        if (validTicketTypes.length > 0) {
          console.log('📤 Sending ticket types:', validTicketTypes);
          const ticketResponse = await fetch(`${API_URL}/events/${eventId}/ticket-types`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ ticketTypes: validTicketTypes })
          });

          if (!ticketResponse.ok) {
            const errorData = await ticketResponse.json().catch(() => ({ message: 'Unknown error' }));
            console.error('❌ Failed to create ticket types:', errorData);
            throw new Error(`Failed to create ticket types: ${errorData.message || 'Unknown error'}`);
          } else {
            const ticketResult = await ticketResponse.json();
            console.log('✅ Tickets created successfully:', ticketResult);
          }
        } else {
          console.warn('⚠️ No valid ticket types to create. Tickets:', ticketTypes);
        }
      }

      // Handle add-ons - only update changed ones
      if (eventToEdit && (addOnChanges.size > 0 || removedAddOns.size > 0)) {
        // First, handle deletions
        for (const removedId of removedAddOns) {
          const response = await fetch(`${API_URL}/events/${eventId}/addons/${removedId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            console.warn('Failed to delete add-on:', removedId);
          }
        }

        // Then handle updates and new creations
        for (const changedId of addOnChanges) {
          const changedAddOn = addOns.find(a => a._id === changedId);
          if (!changedAddOn) continue;

          if (changedAddOn.isNew) {
            // Create new add-on
            const newAddOnData = {
              name: changedAddOn.name,
              description: changedAddOn.description || '',
              price: parseFloat(changedAddOn.price),
              currency: changedAddOn.currency || 'DHS',
              category: changedAddOn.category || 'other',
              maxQuantityPerTicket: parseInt(changedAddOn.maxQuantityPerTicket) || 10,
              totalAvailable: changedAddOn.totalAvailable ? parseInt(changedAddOn.totalAvailable) : null,
              sortOrder: changedAddOn.sortOrder || 0,
              associationType: changedAddOn.associationType || 'global',
              requiresAttendeeAssociation: changedAddOn.requiresAttendeeAssociation || false
            };

            const response = await fetch(`${API_URL}/events/${eventId}/addons`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ addOns: [newAddOnData] })
            });

            if (!response.ok) {
              console.warn('Failed to create new add-on:', changedAddOn.name);
            }
          } else {
            // Update existing add-on
            const originalAddOn = originalAddOns.find(a => a._id === changedId);
            const updateData = {};

            // Only include fields that actually changed
            if (changedAddOn.name !== originalAddOn.name) updateData.name = changedAddOn.name;
            if (changedAddOn.description !== originalAddOn.description) updateData.description = changedAddOn.description || '';
            if (parseFloat(changedAddOn.price) !== parseFloat(originalAddOn.price)) updateData.price = parseFloat(changedAddOn.price);
            if (changedAddOn.currency !== originalAddOn.currency) updateData.currency = changedAddOn.currency || 'DHS';
            if (changedAddOn.category !== originalAddOn.category) updateData.category = changedAddOn.category || 'other';
            if (parseInt(changedAddOn.maxQuantityPerTicket) !== parseInt(originalAddOn.maxQuantityPerTicket)) updateData.maxQuantityPerTicket = parseInt(changedAddOn.maxQuantityPerTicket) || 10;
            
            // Handle totalAvailable which can be null
            const newTotal = changedAddOn.totalAvailable ? parseInt(changedAddOn.totalAvailable) : null;
            const origTotal = originalAddOn.totalAvailable ? parseInt(originalAddOn.totalAvailable) : null;
            if (newTotal !== origTotal) updateData.totalAvailable = newTotal;
            
            if (changedAddOn.associationType !== originalAddOn.associationType) updateData.associationType = changedAddOn.associationType || 'global';
            if (changedAddOn.requiresAttendeeAssociation !== originalAddOn.requiresAttendeeAssociation) updateData.requiresAttendeeAssociation = changedAddOn.requiresAttendeeAssociation || false;
            if (changedAddOn.sortOrder !== originalAddOn.sortOrder) updateData.sortOrder = changedAddOn.sortOrder || 0;

            // Only send update if there are actual changes
            if (Object.keys(updateData).length > 0) {
              const response = await fetch(`${API_URL}/events/${eventId}/addons/${changedId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updateData)
              });

              if (!response.ok) {
                console.warn('Failed to update add-on:', changedAddOn.name);
              }
            }
          }
        }
      } else if (!eventToEdit) {
        // For new events, create all valid add-ons
        const validAddOns = addOns.filter(addOn => 
          addOn.name && addOn.price !== ''
        ).map(addOn => ({
          name: addOn.name,
          description: addOn.description || '',
          price: parseFloat(addOn.price),
          currency: addOn.currency || 'DHS',
          category: addOn.category || 'other',
          maxQuantityPerTicket: parseInt(addOn.maxQuantityPerTicket) || 10,
          totalAvailable: addOn.totalAvailable ? parseInt(addOn.totalAvailable) : null,
          sortOrder: addOn.sortOrder || 0,
          associationType: addOn.associationType || 'global',
          requiresAttendeeAssociation: addOn.requiresAttendeeAssociation || false
        }));

        if (validAddOns.length > 0) {
          const addOnsResponse = await fetch(`${API_URL}/events/${eventId}/addons`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ addOns: validAddOns })
          });

          if (!addOnsResponse.ok) {
            console.warn('Failed to create add-ons for new event');
          }
        }
      }

      onSuccess();
      onClose();
      
      // Reset change tracking
      setTicketTypeChanges(new Set());
      setRemovedTicketTypes(new Set());
      setOriginalTicketTypes([]);
      setAddOnChanges(new Set());
      setRemovedAddOns(new Set());
      setOriginalAddOns([]);
      
      // Reset form
      if (!eventToEdit) {
        setFormData({
          name: '',
          description: '',
          date: '',
          time: '',
          venue: '',
          location: '',
          category: 'general',
          maxCapacity: '',
          image: null
        });
        setTicketTypes([{
          typeName: 'General Admission',
          description: '',
          price: '',
          totalQuantity: '',
          features: [],
          expiryDate: '',
          sortOrder: 0,
          allocationField: '',
          minPersons: 1,
          minAge: 0,
          maxAge: 120
        }]);
        setAddOns([]);
      }

    } catch (error) {
      console.error('Error creating/updating event:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay show={isOpen} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <ModalContent show={isOpen}>
          <ModalHeader>
            <ModalTitle>{eventToEdit ? 'Edit Event' : 'Create New Event'}</ModalTitle>
            <CloseButton onClick={onClose}>
              <FiX size={24} />
            </CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
          <ModalBody>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <FormRow columns="1fr 1fr">
              <FormGroup>
                <Label>
                  <FiCalendar size={16} />
                  Event Name *
                </Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter event name"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Category</Label>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="general">General</option>
                  <option value="music">Music</option>
                  <option value="conference">Conference</option>
                  <option value="workshop">Workshop</option>
                  <option value="sports">Sports</option>
                  <option value="food">Food & Dining</option>
                  <option value="art">Art & Culture</option>
                  <option value="business">Business</option>
                  <option value="education">Education</option>
                  <option value="entertainment">Entertainment</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Description *</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your event..."
                required
              />
            </FormGroup>

            <FormRow columns="1fr 1fr">
              <FormGroup>
                <Label>
                  <FiCalendar size={16} />
                  Date *
                </Label>
                <Input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Time *</Label>
                <Input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </FormRow>

            <FormRow columns="1fr 1fr">
              <FormGroup>
                <Label>
                  <FiMapPin size={16} />
                  Venue *
                </Label>
                <Input
                  type="text"
                  name="venue"
                  value={formData.venue}
                  onChange={handleInputChange}
                  placeholder="Venue name"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Location *</Label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="City, Country"
                  required
                />
              </FormGroup>
            </FormRow>

            <FormRow columns="1fr 1fr">
              <FormGroup>
                <Label>
                  <FiUsers size={16} />
                  Max Capacity
                </Label>
                <Input
                  type="number"
                  name="maxCapacity"
                  value={formData.maxCapacity}
                  onChange={handleInputChange}
                  placeholder="Maximum attendees"
                  min="0"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Event Image</Label>
                <Input
                  type="file"
                  name="image"
                  onChange={handleInputChange}
                  accept="image/*"
                />
              </FormGroup>
            </FormRow>

            <FormRow columns="1fr 1fr">
              <FormGroup>
                <Label>
                  Tax Settings
                </Label>
                <CheckboxWrapper>
                  <CheckboxInput
                    type="checkbox"
                    name="taxEnabled"
                    checked={formData.taxEnabled}
                    onChange={handleInputChange}
                  />
                  <CheckboxLabel>Enable VAT/Tax (Current: {formData.taxEnabled ? 'ON' : 'OFF'})</CheckboxLabel>
                </CheckboxWrapper>
              </FormGroup>
              
              {formData.taxEnabled && (
                <FormGroup>
                  <Label>Tax Rate (%)</Label>
                  <Input
                    type="number"
                    name="taxRate"
                    value={formData.taxRate}
                    onChange={handleInputChange}
                    placeholder="5"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </FormGroup>
              )}

              {/* Reference Name Feature */}
              <FormGroup>
                <Label>
                  Reference Name Feature
                </Label>
                <CheckboxWrapper>
                  <CheckboxInput
                    type="checkbox"
                    name="referenceEnabled"
                    checked={formData.referenceEnabled}
                    onChange={handleInputChange}
                  />
                  <CheckboxLabel>Enable Reference Name Entry (Current: {formData.referenceEnabled ? 'ON' : 'OFF'})</CheckboxLabel>
                </CheckboxWrapper>
                <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                  When enabled, customers will see a modal to enter an optional reference name when booking tickets
                </small>
              </FormGroup>

              {/* Transportation Request Feature */}
              <FormGroup>
                <Label>
                  Transportation Request Feature
                </Label>
                <CheckboxWrapper>
                  <CheckboxInput
                    type="checkbox"
                    name="transportationEnabled"
                    checked={formData.transportationEnabled}
                    onChange={handleInputChange}
                  />
                  <CheckboxLabel>Enable Transportation Requests (Current: {formData.transportationEnabled ? 'ON' : 'OFF'})</CheckboxLabel>
                </CheckboxWrapper>
                <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                  When enabled, customers can request transportation by providing attendee details and pickup location
                </small>
              </FormGroup>

              {/* Invite-Only Security Feature */}
              <FormGroup>
                <Label>
                  🔒 Invite-Only Security Feature
                </Label>
                <CheckboxWrapper>
                  <CheckboxInput
                    type="checkbox"
                    name="inviteOnly"
                    checked={formData.inviteOnly}
                    onChange={handleInputChange}
                  />
                  <CheckboxLabel>Make this event invite-only (Current: {formData.inviteOnly ? 'ON' : 'OFF'})</CheckboxLabel>
                </CheckboxWrapper>
                <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                  When enabled, the event will require a PIN to access and will appear blurred in the event listing
                </small>
                
                {formData.inviteOnly && (
                  <SetupSecurityButton type="button" onClick={() => setShowSecurityModal(true)}>
                    ⚙️ Setup Security Configuration
                  </SetupSecurityButton>
                )}

                {formData.inviteOnly && formData.securityInfo && (
                  <SecurityInfoBox>
                    <strong>Security Info:</strong> {formData.securityInfo}<br/>
                    <strong>PIN Generation:</strong> {formData.pinGenerationFrequency}
                  </SecurityInfoBox>
                )}
              </FormGroup>
            </FormRow>

            <SectionTitle>Ticket Types</SectionTitle>
            
            {ticketTypes.map((ticket, index) => (
              <TicketTypeCard key={index}>
                {ticketTypes.length > 1 && (
                  <RemoveTicketButton
                    type="button"
                    onClick={() => removeTicketType(index)}
                    disabled={ticket.soldQuantity > 0}
                    style={{
                      opacity: ticket.soldQuantity > 0 ? 0.5 : 1,
                      cursor: ticket.soldQuantity > 0 ? 'not-allowed' : 'pointer'
                    }}
                    title={ticket.soldQuantity > 0 
                      ? `Cannot delete: ${ticket.soldQuantity} tickets sold` 
                      : 'Delete ticket type'
                    }
                  >
                    <FiTrash2 size={14} />
                  </RemoveTicketButton>
                )}
                
                <FormRow columns="2fr 1fr">
                  <FormGroup>
                    <Label>Ticket Type Name *</Label>
                    <Input
                      type="text"
                      value={ticket.typeName}
                      onChange={(e) => handleTicketTypeChange(index, 'typeName', e.target.value)}
                      placeholder="e.g., General Admission, VIP, Early Bird"
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>
                      <FiDollarSign size={16} />
                      Price (DHS) *
                    </Label>
                    <Input
                      type="number"
                      value={ticket.price}
                      onChange={(e) => handleTicketTypeChange(index, 'price', e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Description</Label>
                  <TextArea
                    value={ticket.description}
                    onChange={(e) => handleTicketTypeChange(index, 'description', e.target.value)}
                    placeholder="Describe what's included with this ticket type..."
                    style={{ minHeight: '80px' }}
                  />
                </FormGroup>

                <FormRow columns="1fr 1fr 1fr">
                  <FormGroup>
                    <Label>Total Quantity *</Label>
                    <Input
                      type="number"
                      value={ticket.totalQuantity}
                      onChange={(e) => handleTicketTypeChange(index, 'totalQuantity', e.target.value)}
                      placeholder="Number of tickets available"
                      min={ticket.soldQuantity || 1}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Tickets Sold</Label>
                    <Input
                      type="text"
                      value={`${ticket.soldQuantity || 0} sold`}
                      disabled
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        color: ticket.soldQuantity > 0 ? '#dc3545' : '#6c757d',
                        fontWeight: ticket.soldQuantity > 0 ? 'bold' : 'normal'
                      }}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Expiry Date (Optional)</Label>
                    <Input
                      type="datetime-local"
                      value={ticket.expiryDate}
                      onChange={(e) => handleTicketTypeChange(index, 'expiryDate', e.target.value)}
                    />
                  </FormGroup>
                </FormRow>

                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <Label>Allocation Field (Admin Use)</Label>
                    <select
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: '#fff',
                        color: '#333',
                        cursor: 'pointer'
                      }}
                      value={ticket.allocationField}
                      onChange={(e) => handleTicketTypeChange(index, 'allocationField', e.target.value)}
                    >
                      <option value="">Select allocation type</option>
                      <option value="men">Men</option>
                      <option value="woman">Woman</option>
                      <option value="couples">Couples</option>
                    </select>
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Used for internal tracking and allocation management
                    </small>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Minimum Persons Required *</Label>
                    <Input
                      type="number"
                      value={ticket.minPersons}
                      onChange={(e) => handleTicketTypeChange(index, 'minPersons', e.target.value)}
                      placeholder="1"
                      min="1"
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Users cannot purchase fewer than this number
                    </small>
                  </FormGroup>
                </FormRow>

                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <Label>Minimum Age</Label>
                    <Input
                      type="number"
                      value={ticket.minAge}
                      onChange={(e) => handleTicketTypeChange(index, 'minAge', e.target.value)}
                      placeholder="0"
                      min="0"
                      max="120"
                    />
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Minimum age required for this ticket
                    </small>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Maximum Age</Label>
                    <Input
                      type="number"
                      value={ticket.maxAge}
                      onChange={(e) => handleTicketTypeChange(index, 'maxAge', e.target.value)}
                      placeholder="120"
                      min="0"
                      max="120"
                    />
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Maximum age allowed for this ticket
                    </small>
                  </FormGroup>
                </FormRow>
              </TicketTypeCard>
            ))}

            <AddTicketButton type="button" onClick={addTicketType}>
              <FiPlus size={16} />
              Add Another Ticket Type
            </AddTicketButton>

            {/* Add-ons Section */}
            <SectionTitle>Add-ons (Optional)</SectionTitle>
            
            {addOns.map((addOn, index) => (
              <TicketTypeCard key={index}>
                <CardHeader>
                  <h4>Add-on #{index + 1}</h4>
                  <RemoveTicketButton
                    type="button"
                    onClick={() => removeAddOn(index)}
                  >
                    <FiTrash2 size={16} />
                  </RemoveTicketButton>
                </CardHeader>

                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <Label>Add-on Name *</Label>
                    <Input
                      type="text"
                      value={addOn.name}
                      onChange={(e) => handleAddOnChange(index, 'name', e.target.value)}
                      placeholder="e.g., T-Shirt, Food Package"
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>
                      <FiDollarSign size={16} />
                      Price (DHS) *
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={addOn.price}
                      onChange={(e) => handleAddOnChange(index, 'price', e.target.value)}
                      placeholder="0.00"
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Description</Label>
                  <TextArea
                    value={addOn.description}
                    onChange={(e) => handleAddOnChange(index, 'description', e.target.value)}
                    placeholder="Describe this add-on..."
                    rows="2"
                  />
                </FormGroup>

                <FormRow columns="1fr 1fr 1fr">
                  <FormGroup>
                    <Label>Category</Label>
                    <Select
                      value={addOn.category}
                      onChange={(e) => handleAddOnChange(index, 'category', e.target.value)}
                    >
                      <option value="merchandise">Merchandise</option>
                      <option value="food">Food & Beverage</option>
                      <option value="service">Service</option>
                      <option value="access">Special Access</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Max Per Ticket</Label>
                    <Input
                      type="number"
                      min="1"
                      value={addOn.maxQuantityPerTicket}
                      onChange={(e) => handleAddOnChange(index, 'maxQuantityPerTicket', e.target.value)}
                      placeholder="10"
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Total Available</Label>
                    <Input
                      type="number"
                      min="1"
                      value={addOn.totalAvailable || ''}
                      onChange={(e) => handleAddOnChange(index, 'totalAvailable', e.target.value)}
                      placeholder="Unlimited"
                    />
                  </FormGroup>
                </FormRow>

                {/* Attendee Association Settings */}
                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <Label>Association Type</Label>
                    <Select
                      value={addOn.associationType || 'global'}
                      onChange={(e) => handleAddOnChange(index, 'associationType', e.target.value)}
                    >
                      <option value="global">🌐 Global - Available to all</option>
                      <option value="per-ticket">🎫 Per Ticket - One per ticket type</option>
                      <option value="per-attendee">👤 Per Attendee - Individual assignment</option>
                    </Select>
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Global: Shows on all tickets. Per-attendee: Must be assigned to specific attendees.
                    </small>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={addOn.requiresAttendeeAssociation || false}
                        onChange={(e) => handleAddOnChange(index, 'requiresAttendeeAssociation', e.target.checked)}
                        disabled={addOn.associationType !== 'per-attendee'}
                        style={{ 
                          width: '16px', 
                          height: '16px',
                          opacity: addOn.associationType !== 'per-attendee' ? 0.5 : 1
                        }}
                      />
                      Require Attendee Selection
                    </Label>
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {addOn.associationType === 'per-attendee' 
                        ? 'When checked, customers must select which attendee gets this add-on'
                        : 'Only available for per-attendee association type'
                      }
                    </small>
                  </FormGroup>
                </FormRow>
              </TicketTypeCard>
            ))}

            <AddTicketButton type="button" onClick={addAddOn}>
              <FiPlus size={16} />
              Add Event Add-on
            </AddTicketButton>
          </ModalBody>

          <ModalFooter>
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (eventToEdit ? 'Updating...' : 'Creating...') : (eventToEdit ? 'Update Event' : 'Create Event')}
            </Button>
          </ModalFooter>
        </Form>
      </ModalContent>
    </ModalOverlay>

    {/* Security Setup Modal */}
    <SecurityModalOverlay show={showSecurityModal}>
      <SecurityModalContent show={showSecurityModal}>
        <ModalHeader>
          <ModalTitle>🔒 Security Configuration</ModalTitle>
          <CloseButton onClick={() => setShowSecurityModal(false)}>
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormGroup>
            <Label>Custom Security Information *</Label>
            <TextArea
              value={formData.securityInfo}
              onChange={(e) => setFormData(prev => ({ ...prev, securityInfo: e.target.value }))}
              placeholder="Enter custom message that will be displayed to users when they click the info icon on the blurred event..."
              rows={4}
              required
            />
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              This message will be shown when users click the information icon on the invite-only event
            </small>
          </FormGroup>

          <FormGroup>
            <Label>PIN Notification Email *</Label>
            <Input
              type="email"
              value={formData.pinNotificationEmail || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, pinNotificationEmail: e.target.value }))}
              placeholder="admin@company.com"
              required
            />
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              PIN notifications will be sent to this email address when generated or requested
            </small>
          </FormGroup>

          <FormGroup>
            <Label>PIN Generation Frequency</Label>
            <Select
              value={formData.pinGenerationFrequency}
              onChange={(e) => setFormData(prev => ({ ...prev, pinGenerationFrequency: e.target.value }))}
            >
              <option value="hourly">Hourly - New PIN every hour</option>
              <option value="daily">Daily - New PIN every day</option>
              <option value="weekly">Weekly - New PIN every week</option>
              <option value="monthly">Monthly - New PIN every month</option>
              <option value="once">Once - PIN never changes</option>
              <option value="event">Event Duration - Same PIN for entire event</option>
            </Select>
            <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
              Choose how often the access PIN should be regenerated. You will receive email notifications when new PINs are generated.
            </small>
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button type="button" onClick={() => setShowSecurityModal(false)}>
            Cancel
          </Button>
          <Button 
            type="button" 
            variant="primary" 
            onClick={() => setShowSecurityModal(false)}
            disabled={!formData.securityInfo.trim()}
          >
            Save Security Settings
          </Button>
        </ModalFooter>
      </SecurityModalContent>
    </SecurityModalOverlay>
    </>
  );
};

export default CreateEventModal;
